"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.themeByName = themeByName;
exports.isKnownTheme = isKnownTheme;
exports.variantsOf = variantsOf;
exports.defaultVariantOf = defaultVariantOf;
exports.variantOf = variantOf;
exports.isKnownVariant = isKnownVariant;
exports.normalizeAppearance = normalizeAppearance;
exports.invalidateThemeSummaries = invalidateThemeSummaries;
exports.themeSummaries = themeSummaries;
exports.brandSwatches = brandSwatches;
const tokens_1 = require("./tokens");
const contrast_1 = require("./contrast");
const resolve_1 = require("./resolve");
/** 未知主题名一律回退到默认主题，并让调用方知道发生了回退 */
function themeByName(name) {
    if (name && tokens_1.THEMES[name])
        return tokens_1.THEMES[name];
    return tokens_1.THEMES[tokens_1.DEFAULT_THEME];
}
function isKnownTheme(name) {
    return typeof name === 'string' && Boolean(tokens_1.THEMES[name]);
}
function variantsOf(name) {
    const theme = themeByName(name);
    return Object.values(theme.variants).map((v) => ({ key: v.key, label: v.label }));
}
/** 该主题的第一个变体。库里 variant 是 NOT NULL，回退必须有确定答案 */
function defaultVariantOf(name) {
    const theme = themeByName(name);
    return Object.keys(theme.variants)[0] ?? 'default';
}
function variantOf(name, variant) {
    const theme = themeByName(name);
    if (variant && theme.variants[variant])
        return variant;
    return Object.keys(theme.variants)[0] ?? 'default';
}
function isKnownVariant(name, variant) {
    if (typeof variant !== 'string')
        return false;
    return Boolean(themeByName(name).variants[variant]);
}
/**
 * 把任意来源的 (theme, variant) 归一成可渲染的一对。
 *
 * 为什么不做成"非法就报错"：渲染层拿到的是库里三个月前的数据，
 * 主题被下架、变体被改名都可能发生。那时候让客户站点 500 是最坏的选择，
 * 回退到默认主题并打 warn 才是。
 */
function normalizeAppearance(themeName, variantKey) {
    const warnings = [];
    let theme = tokens_1.DEFAULT_THEME;
    if (isKnownTheme(themeName)) {
        theme = themeName;
    }
    else if (themeName) {
        warnings.push(`未知主题 ${themeName}，已回退到 ${tokens_1.DEFAULT_THEME}`);
    }
    const manifest = tokens_1.THEMES[theme];
    const fallback = Object.keys(manifest.variants)[0] ?? 'default';
    let variant = fallback;
    if (typeof variantKey === 'string' && manifest.variants[variantKey]) {
        variant = variantKey;
    }
    else if (variantKey) {
        warnings.push(`主题 ${theme} 没有变体 ${variantKey}，已回退到 ${fallback}`);
    }
    return { theme, variant, warnings };
}
/**
 * 控制台主题卡片的数据。
 *
 * 只算一次并缓存：这套数据含两次 `resolveTheme`（几十个令牌 + 对比度），
 * 而它只在控制台打开时才需要，没必要每次请求都重算。
 */
let cached = null;
/**
 * 清掉摘要缓存。
 *
 * 主题从"编译期常量"变成"内置四套 + 运行时发布的若干套"之后，这份缓存
 * 不能再假设清单是静态的：发布一套新主题后不清它，市场页刷十次也看不到。
 */
function invalidateThemeSummaries() {
    cached = null;
}
function themeSummaries() {
    if (cached)
        return cached;
    cached = Object.values(tokens_1.THEMES).map((manifest) => {
        const resolved = (0, resolve_1.resolveTheme)({ manifest });
        // 判"标题是不是衬线"要看令牌的**引用**，不能看解析后的字体栈：
        // sans 栈里本来就带 'Noto Serif SC' 这个中文字体兜底，用 includes('serif') 会全是 true
        const flatSemantic = (0, resolve_1.flattenTokens)(manifest.tokens.semantic);
        const headingRef = flatSemantic['font.heading'];
        return {
            name: manifest.name,
            version: manifest.version,
            archetype: manifest.archetype ?? tokens_1.DEFAULT_ARCHETYPE,
            label: manifest.label ?? { zh: manifest.name, en: manifest.name },
            description: manifest.description ?? { zh: '', en: '' },
            variants: variantsOf(manifest.name),
            defaultVariant: defaultVariantOf(manifest.name),
            palette: {
                bg: resolved.vars['--color-bg'] ?? '#ffffff',
                surface: resolved.vars['--color-surface'] ?? '#f7f9fc',
                text: resolved.vars['--color-text'] ?? '#161d26',
                brand: resolved.vars['--color-brand'] ?? '#1f57d6',
                accent: resolved.vars['--color-accent'] ?? '#e8a33d',
                border: resolved.vars['--color-border'] ?? '#dde4ee',
                headerBg: resolved.vars['--color-header-bg'] ?? '#ffffff',
                headerText: resolved.vars['--color-header-text'] ?? '#161d26',
            },
            serifHeading: headingRef === '{font.serif}',
            preview: manifest.preview,
            monthlyCents: manifest.pricing?.monthlyCents ?? 0,
            paid: (manifest.pricing?.monthlyCents ?? 0) > 0,
            listed: manifest.listed !== false,
        };
    });
    return cached;
}
const SWATCHES = [
    { hex: '#1f57d6', label: { zh: '极光蓝', en: 'Aurora blue' } },
    { hex: '#0f3d6e', label: { zh: '深海蓝', en: 'Deep sea' } },
    { hex: '#0e7f8c', label: { zh: '青碧', en: 'Teal' } },
    { hex: '#2f6f4f', label: { zh: '墨绿', en: 'Forest' } },
    { hex: '#7a5c2e', label: { zh: '土棕', en: 'Umber' } },
    { hex: '#b8232f', label: { zh: '中国红', en: 'Crimson' } },
    { hex: '#c2521e', label: { zh: '赭橙', en: 'Burnt orange' } },
    { hex: '#d98b1f', label: { zh: '琥珀', en: 'Amber' } },
    { hex: '#b03a5b', label: { zh: '玫红', en: 'Rose' } },
    { hex: '#6d3bb8', label: { zh: '紫罗兰', en: 'Violet' } },
    { hex: '#5b6879', label: { zh: '石板灰', en: 'Slate' } },
    { hex: '#161d26', label: { zh: '石墨黑', en: 'Graphite' } },
];
let swatchCache = null;
function brandSwatches() {
    if (swatchCache)
        return swatchCache;
    swatchCache = SWATCHES.map((s) => {
        const onBrand = (0, contrast_1.suggestOnBrand)(s.hex);
        return {
            ...s,
            onBrand,
            contrast: Math.round(((0, contrast_1.contrastRatio)(onBrand, s.hex) ?? 0) * 100) / 100,
        };
    });
    return swatchCache;
}
//# sourceMappingURL=catalog.js.map